import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import {
  SecurityOutlined as SecurityIcon,
  AccountTreeOutlined as SchemaDiagramIcon,
  TableChartOutlined as SchemaTableIcon,
  ViewListOutlined as DetailedRulesIcon,
  PlayArrowOutlined as PlayArrowIcon,
  HistoryOutlined as HistoryIcon,
  AltRouteOutlined as ForkIcon,
  PendingActionsOutlined as PendingActionsIcon,
  LinkOutlined as ConnectionIcon,
  VpnKeyOutlined as VpnKeyIcon,
  SettingsOutlined as SettingsIcon,
  PhotoLibraryOutlined as SnapshotsIcon,
  ShowChartOutlined as StatisticsIcon,
  PeopleOutlined as UsersIcon,
  AutoAwesomeOutlined as ClassificationIcon,
  CloudUploadOutlined as UpstreamIcon,
  CloudDownloadOutlined as DownstreamIcon,
  TuneOutlined as ParametersIcon,
} from '@mui/icons-material'
import { useDatabaseSidebarMeta } from '../../hooks/useDatabaseSidebarMeta'
import {
  getDatabaseNavItems,
  getDatabaseSchemaNavSubItems,
  getDatabaseConnectionNavSubItems,
  isDatabaseNavItemActive,
  isDatabaseSchemaNavItemActive,
  isDatabaseSchemaSubItemActive,
  isDatabaseConnectionNavItemActive,
  isDatabaseConnectionSubItemActive,
  databaseSchemaNavItemPath,
  databaseConnectionNavItemPath,
  type DatabaseNavItem,
  type DatabaseNavItemKind,
  type DatabaseSchemaNavSubItem,
  type DatabaseConnectionNavSubItem,
} from '../../utils/databaseContextNav'
import { DATABASE_UI } from '../../constants/databaseUIMessages'
import { isDatabaseSchemaPath } from '../../utils/databaseSchemaNav'
import { isDatabaseConnectionPath } from '../../utils/databaseConnectionNav'
import { NavItem } from './sidebarPrimitives'

function iconForKind(kind: DatabaseNavItemKind): React.ReactNode {
  switch (kind) {
    case 'sensitive_columns':
      return <SecurityIcon />
    case 'detailed_rules':
      return <DetailedRulesIcon />
    case 'preview_queries':
      return <PlayArrowIcon />
    case 'query_history':
      return <HistoryIcon />
    case 'forks':
      return <ForkIcon />
    case 'pending_writes':
      return <PendingActionsIcon />
    case 'connection':
      return <ConnectionIcon />
    case 'keys':
      return <VpnKeyIcon />
    case 'general':
      return <SettingsIcon />
    case 'schema':
      return <SchemaDiagramIcon />
    case 'snapshots':
      return <SnapshotsIcon />
    case 'statistics':
      return <StatisticsIcon />
    case 'users':
      return <UsersIcon />
    case 'classification':
      return <ClassificationIcon />
    default:
      return <SchemaDiagramIcon />
  }
}

function iconForSchemaView(view: DatabaseSchemaNavSubItem['view']): React.ReactNode {
  return view === 'table'
    ? <SchemaTableIcon />
    : <SchemaDiagramIcon />
}

function iconForConnectionView(view: DatabaseConnectionNavSubItem['view']): React.ReactNode {
  switch (view) {
    case 'upstream':
      return <UpstreamIcon />
    case 'downstream':
      return <DownstreamIcon />
    case 'parameters':
      return <ParametersIcon />
  }
}

function renderNavItem(
  item: DatabaseNavItem,
  flags: ReturnType<typeof useDatabaseSidebarMeta>['flags'],
  pathname: string,
  search: string,
  go: (path: string) => void,
) {
  const label =
    item.kind === 'detailed_rules' ? DATABASE_UI.tab_detailedRules
    : item.label
  return (
    <NavItem
      key={`${item.kind}-${item.path}`}
      text={label}
      icon={iconForKind(item.kind)}
      active={isDatabaseNavItemActive(pathname, search, item, flags)}
      onClick={() => go(item.path)}
      badge={item.badge}
    />
  )
}

export default function DatabaseContextNav({
  databaseId,
  onNavigate,
}: {
  databaseId: string
  onNavigate?: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { flags, pendingWritesCount } = useDatabaseSidebarMeta(databaseId)

  const items = getDatabaseNavItems(databaseId, flags, pendingWritesCount)
  const schemaSubItems = getDatabaseSchemaNavSubItems(databaseId)
  const connectionSubItems = getDatabaseConnectionNavSubItems(databaseId)
  const schemaPath = databaseSchemaNavItemPath(databaseId)
  const connectionPath = databaseConnectionNavItemPath(databaseId)

  const detailedRulesIndex = items.findIndex((item) => item.kind === 'detailed_rules')
  const beforeSchema = detailedRulesIndex === -1 ? items : items.slice(0, detailedRulesIndex)
  const afterSchema = detailedRulesIndex === -1 ? [] : items.slice(detailedRulesIndex)

  const go = (path: string) => {
    navigate(path)
    onNavigate?.()
  }

  const onSchemaRoute = isDatabaseSchemaPath(location.pathname, databaseId)
  const onConnectionRoute = isDatabaseConnectionPath(location.pathname, databaseId)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <Box sx={{ flex: 1 }}>
        {beforeSchema.map((item) => renderNavItem(item, flags, location.pathname, location.search, go))}

        <NavItem
          text="Schema"
          icon={<SchemaDiagramIcon />}
          active={onSchemaRoute && isDatabaseSchemaNavItemActive(location.pathname, databaseId)}
          onClick={() => go(schemaPath)}
        />
        {onSchemaRoute ? (
          <Box sx={{ ml: 1.5, borderLeft: '1px solid var(--vs-border-subtle)', pl: 0.5 }}>
            {schemaSubItems.map((sub) => (
              <NavItem
                key={sub.view}
                text={sub.label}
                icon={iconForSchemaView(sub.view)}
                active={isDatabaseSchemaSubItemActive(location.pathname, databaseId, sub.view)}
                onClick={() => go(sub.path)}
                pl={1.5}
                fontSize="0.8125rem"
              />
            ))}
          </Box>
        ) : null}

        {afterSchema.map((item) => {
          if (item.kind === 'connection') {
            return (
              <React.Fragment key={`${item.kind}-${item.path}`}>
                <NavItem
                  text={item.label}
                  icon={iconForKind(item.kind)}
                  active={onConnectionRoute && isDatabaseConnectionNavItemActive(location.pathname, databaseId)}
                  onClick={() => go(connectionPath)}
                />
                {onConnectionRoute ? (
                  <Box sx={{ ml: 1.5, borderLeft: '1px solid var(--vs-border-subtle)', pl: 0.5 }}>
                    {connectionSubItems.map((sub) => (
                      <NavItem
                        key={sub.view}
                        text={sub.label}
                        icon={iconForConnectionView(sub.view)}
                        active={isDatabaseConnectionSubItemActive(location.pathname, databaseId, sub.view)}
                        onClick={() => go(sub.path)}
                        pl={1.5}
                        fontSize="0.8125rem"
                      />
                    ))}
                  </Box>
                ) : null}
              </React.Fragment>
            )
          }
          return renderNavItem(item, flags, location.pathname, location.search, go)
        })}
      </Box>
    </Box>
  )
}
